import { CheckBoxButtonsGroup } from 'components/common-form/builder/form/node/field/CheckboxField';
import { AmountWithUnit } from 'components/shared/Amount/Amount';
import { RadioButtonsSkeleton } from 'components/shared/ButtonsGroup/ButtonsGroup';
import { BlockOrientationFormGroup } from 'components/shared/FormGroup';
import { FormattedMessage } from 'components/shared/Intl';
import { TextField, TextFieldSkeleton } from 'components/shared/TextField/TextField';
import { ChangeEvent, FC, useContext } from 'react';
import { Card, CardBody, CardTitle } from 'react-bootstrap';
import type { BackToBackType } from 'state/uiPanelState/tradeConfirmation/tradeConfirm.model';
import { camelCaseToWords } from 'utils/string';

import { AddonMainFormContext } from '../AddonMainForm.context';

const EDITABLE_BACK_TO_BACK_TYPES: BackToBackType[] = ['None', 'ClientTrade'];
const YES_NO_ITEMS = ['Yes', 'No'];

type BackToBackBlockProps = {
  currency?: string;
  fileCode?: string;
  isBackToBack: boolean;
  isBackToBackReadonly: boolean;
  isBackToBackTypeReadonly: boolean;
  isDelayedBackToBack?: boolean;
  isDeltaAdjusted?: boolean;
  noteNominal?: number;
  onFileCodeChange: (value: string | undefined) => void;
  onIsBackToBackChange: (value: boolean) => void;
  onIsDelayedBackToBackChange: (value: boolean | undefined) => void;
  onIsDeltaAdjustedChange: (value: boolean | undefined) => void;
  onNoteNominalChange: (value: number | undefined) => void;
  onTypeChange: (value: BackToBackType) => void;
  type: BackToBackType;
};

export const BackToBackBlock: FC<BackToBackBlockProps> = ({
  currency,
  fileCode,
  isBackToBack,
  isBackToBackReadonly,
  isBackToBackTypeReadonly,
  isDelayedBackToBack,
  isDeltaAdjusted,
  noteNominal,
  onFileCodeChange,
  onIsBackToBackChange,
  onIsDelayedBackToBackChange,
  onIsDeltaAdjustedChange,
  onNoteNominalChange,
  onTypeChange,
  type,
}) => {
  const { isLoading, isReadOnly } = useContext(AddonMainFormContext);
  const isHedgingTrade = type === 'HedgingTrade';
  const isBackToBackTypeisNotNone = type !== 'None';
  const availableTypes = isBackToBackTypeReadonly ? [type] : EDITABLE_BACK_TO_BACK_TYPES;
  const delayedFieldColumnClassName = isHedgingTrade ? 'col-12 col-md-6' : 'col-12';
  const handleTypeChange = (event: ChangeEvent<HTMLSelectElement>) => {
    onTypeChange(event.target.value as BackToBackType);
  };

  if (isLoading) {
    return (
      <Card>
        <CardBody className="py-3">
          <CardTitle as="h6">
            <div className="d-flex align-items-end">
              <FormattedMessage id="addon.backToBack.title" />
            </div>
          </CardTitle>
          <div className="row">
            <div className="col-12 col-md-6">
              <BlockOrientationFormGroup
                inputId={'backToBackIsBackToBack'}
                label={'form.BackToBackIsBackToBack'}
                workspaceType={'postTrade'}
              >
                <RadioButtonsSkeleton items={YES_NO_ITEMS} />
              </BlockOrientationFormGroup>
            </div>
            <div className="col-12 col-md-6">
              <BlockOrientationFormGroup
                inputId={'backToBackType'}
                label={'form.BackToBackType'}
                workspaceType={'postTrade'}
              >
                <TextFieldSkeleton />
              </BlockOrientationFormGroup>
            </div>
          </div>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card>
      <CardBody className="py-3">
        <CardTitle as="h6">
          <div className="d-flex align-items-end">
            <FormattedMessage id="addon.backToBack.title" />
          </div>
        </CardTitle>
        <div className="row">
          <div className="col-12 col-md-6">
            <BlockOrientationFormGroup
              inputId={'backToBackIsBackToBack'}
              label={'form.BackToBackIsBackToBack'}
              workspaceType={'postTrade'}
            >
              <CheckBoxButtonsGroup
                id="backToBackIsBackToBack"
                value={isBackToBack}
                isReadonly={isReadOnly || isBackToBackReadonly}
                onChange={value => onIsBackToBackChange(value ?? false)}
                translationPrefix="form"
              />
            </BlockOrientationFormGroup>
          </div>
          <div className="col-12 col-md-6">
            <BlockOrientationFormGroup
              inputId={'backToBackType'}
              label={'form.BackToBackType'}
              workspaceType={'postTrade'}
            >
              <select
                id="backToBackType"
                className="form-select"
                value={type}
                onChange={handleTypeChange}
                disabled={isReadOnly || isBackToBackTypeReadonly}
              >
                {availableTypes.map(option => (
                  <option key={option} value={option}>
                    {camelCaseToWords(option)}
                  </option>
                ))}
              </select>
            </BlockOrientationFormGroup>
          </div>
          {!isHedgingTrade && isBackToBackTypeisNotNone && isBackToBack ? (
            <div className={delayedFieldColumnClassName}>
              <BlockOrientationFormGroup
                inputId={'backToBackIsDelayedBackToBack'}
                label={'form.IsDelayedBackToBack'}
                workspaceType={'postTrade'}
              >
                <CheckBoxButtonsGroup
                  id="backToBackIsDelayedBackToBack"
                  value={isDelayedBackToBack ?? null}
                  isReadonly={isReadOnly}
                  isNullable={true}
                  onChange={value => onIsDelayedBackToBackChange(value ?? undefined)}
                  translationPrefix="form"
                />
              </BlockOrientationFormGroup>
            </div>
          ) : null}
          {isHedgingTrade && (
            <>
              <div className="col-12 col-md-6">
                <BlockOrientationFormGroup
                  inputId={'backToBackFileCode'}
                  label={'form.BackToBackFileCode'}
                  workspaceType={'postTrade'}
                >
                  <TextField
                    id="backToBackFileCode"
                    value={fileCode}
                    isReadOnly={isReadOnly}
                    onChange={event => onFileCodeChange(event.target.value || undefined)}
                  />
                </BlockOrientationFormGroup>
              </div>
              <div className="col-12 col-md-6">
                <BlockOrientationFormGroup
                  inputId={'backToBackNoteNominal'}
                  label={'form.NoteNominal'}
                  workspaceType={'postTrade'}
                >
                  <AmountWithUnit
                    id="backToBackNoteNominal"
                    value={noteNominal}
                    isDisabled={isReadOnly}
                    onChange={onNoteNominalChange}
                    unit={currency}
                  />
                </BlockOrientationFormGroup>
              </div>
              <div className="col-12 col-md-6">
                <BlockOrientationFormGroup
                  inputId={'backToBackIsDeltaAdjusted'}
                  label={'form.IsDeltaAdjusted'}
                  workspaceType={'postTrade'}
                >
                  <CheckBoxButtonsGroup
                    id="backToBackIsDeltaAdjusted"
                    value={isDeltaAdjusted ?? null}
                    isReadonly={isReadOnly}
                    isNullable={true}
                    onChange={value => onIsDeltaAdjustedChange(value ?? undefined)}
                    translationPrefix="form"
                  />
                </BlockOrientationFormGroup>
              </div>
              <div className={delayedFieldColumnClassName}>
                <BlockOrientationFormGroup
                  inputId={'backToBackIsDelayedBackToBack'}
                  label={'form.IsDelayedBackToBack'}
                  workspaceType={'postTrade'}
                >
                  <CheckBoxButtonsGroup
                    id="backToBackIsDelayedBackToBack"
                    value={isDelayedBackToBack ?? null}
                    isReadonly={isReadOnly}
                    isNullable={true}
                    onChange={value => onIsDelayedBackToBackChange(value ?? undefined)}
                    translationPrefix="form"
                  />
                </BlockOrientationFormGroup>
              </div>
            </>
          )}
        </div>
      </CardBody>
    </Card>
  );
};
